import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import 'aos/dist/aos.css';
import AOS from 'aos';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsAndConditions = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const itemRefs = useRef([]);
  const videoRef = useRef(null);

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Initialize AOS
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-in-out',
    });

    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', 'G-P2TS4436R5');

    // Intersection Observer for animated items
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    itemRefs.current.forEach((item) => {
      if (item) observer.observe(item);
    });

    // Parallax effect (videoRef kept for future if you re-add video)
    const handleScroll = () => {
      if (videoRef.current) {
        const scrollPosition = window.scrollY;
        const translateY = scrollPosition * 0.3;
        videoRef.current.style.transform = `translateY(${translateY}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      itemRefs.current.forEach((item) => {
        if (item) observer.unobserve(item);
      });
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Terms &amp; Conditions | SecurXpert Technologies</title>
        <meta
          name="description"
          content="Read the Terms and Conditions for using SecurXpert Technologies Pvt Ltd’s website, applications, software products and digital services."
        />
        <meta
          name="keywords"
          content="SecurXpert Terms and Conditions, SecurXpert legal, SecurXpert policies, software terms, digital services terms"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://securxperts.com/terms" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Terms and Conditions',
            url: 'https://securxperts.com/terms',
            publisher: {
              '@type': 'Organization',
              name: 'SecurXpert Technologies',
              url: 'https://securxperts.com',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Hyderabad',
                addressCountry: 'IN',
              },
            },
          })}
        </script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <header className="relative w-full">
        <div
          className="
            relative w-full h-[317px]
            bg-no-repeat bg-cover
            bg-[center_10%]
          "
          style={{ backgroundImage: "url(/img/termsnconditions.png)" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-md">
              Terms And Conditions
            </h1>

            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              ></svg>
              <span>Terms And Conditions</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Effective Date + Intro */}
          <h2
            className="text-center text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-blue-900 no-underline mb-4 sm:mb-6 tracking-tight"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Terms and Conditions
          </h2>

          <p
            className="text-center text-sm xs:text-base sm:text-lg font-medium text-gray-800 no-underline mb-3"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            Effective Date: 18/02/2025
          </p>

          <p
            className="text-sm xs:text-base sm:text-lg font-normal text-gray-800 no-underline mb-8 sm:mb-12 text-center leading-relaxed"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Welcome to SecurXpert Technologies Pvt Ltd. These Terms and Conditions
            explain the rules for using our website, mobile applications, products
            and digital services. By accessing or using our platform, you agree to
            follow these terms. If you do not agree, please stop using our services
            immediately.
          </p>

          {[
            {
              title: '1. Introduction',
              content: (
                <>
                  These Terms and Conditions form a legally binding agreement between
                  you and SecurXpert Technologies Pvt Ltd. Continued use of our
                  platform means you accept these terms and all future updates.
                </>
              ),
            },
            {
              title: '2. Definitions',
              content: (
                <>
                  <strong>“Company,” “we,” “our,” “us”</strong> – SecurXpert
                  Technologies Pvt Ltd
                  <br />
                  <br />
                  <strong>“User,” “you,” “your”</strong> – Any individual or entity
                  accessing our services
                  <br />
                  <br />
                  <strong>“Services”</strong> – Our website, mobile applications,
                  software solutions, products and any related offerings
                </>
              ),
            },
            {
              title: '3. Eligibility &amp; User Responsibilities',
              content: (
                <>
                  Users must be 18 years or older. Minors should use the platform
                  under parental or guardian supervision.
                  <br />
                  <br />
                  Users must provide accurate and complete information during
                  registration and keep login credentials confidential.
                  <br />
                  <br />
                  All activity performed under your account is treated as your
                  responsibility.
                </>
              ),
            },
            {
              title: '4. Medical Disclaimer',
              content: (
                <>
                  Content, recommendations and AI-driven responses on our platform
                  are for general information only.
                  <br />
                  <br />
                  They are not a replacement for medical advice, diagnosis or
                  treatment.
                  <br />
                  <br />
                  Always consult a qualified healthcare professional for medical
                  concerns.
                </>
              ),
            },
            {
              title: '5. Ordering, Products &amp; Subscription Services',
              content: (
                <>
                  We offer product ordering, digital services, subscription plans and
                  lab test bookings.
                  <br />
                  <br />
                  All orders depend on availability, prescription validation (where
                  applicable) and compliance with regional laws.
                  <br />
                  <br />
                  Subscription plans renew automatically unless canceled before the
                  renewal date.
                </>
              ),
            },
            {
              title: '6. Payments &amp; Pricing',
              content: (
                <>
                  All payments must be completed via the authorized payment modes
                  available on the platform.
                  <br />
                  <br />
                  Prices and fees may change at our discretion.
                  <br />
                  <br />
                  Unless explicitly stated, subscription and service fees are
                  non-refundable.
                </>
              ),
            },
            {
              title: '7. Delivery Policy',
              content: (
                <>
                  We offer hyperlocal delivery and digital fulfillment where
                  applicable.
                  <br />
                  <br />
                  Delivery timelines are estimates and may vary due to logistics,
                  third-party delays, regulatory checks or external issues.
                </>
              ),
            },
            {
              title: '8. User Conduct &amp; Restrictions',
              content: (
                <>
                  Users agree:
                  <br />
                  <br />
                  • Not to engage in fraudulent, abusive or illegal activities
                  <br />
                  • Not to copy, sell, modify, share or exploit our platform content
                  without written permission
                  <br />
                  • Not to interfere with the security or functionality of our
                  website or applications
                  <br />
                  <br />
                  We reserve the right to suspend or terminate user accounts involved
                  in misuse or policy violations.
                </>
              ),
            },
            {
              title: '9. Intellectual Property',
              content: (
                <>
                  All content, trademarks, designs, features, code, visual elements
                  and branding belong exclusively to SecurXpert Technologies Pvt Ltd.
                  <br />
                  <br />
                  Unauthorized usage, reproduction, distribution or modification is
                  strictly prohibited.
                </>
              ),
            },
            {
              title: '10. Limitation of Liability',
              content: (
                <>
                  SecurXpert provides its website, apps, products and services on an
                  “as-is” and “as-available” basis. While we aim for reliable and
                  seamless service, we do not guarantee uninterrupted or error-free
                  operation.
                  <br />
                  <br />
                  To the maximum extent permitted by law:
                  <br />
                  <br />
                  • SecurXpert is not liable for indirect, incidental,
                  consequential or special damages, including losses from service
                  interruptions, data errors, delays, inaccuracies or third-party
                  issues.
                  <br />
                  • Product and medical information is for informational purposes
                  only and should not replace professional consultations.
                  <br />
                  • Use of the platform is entirely at your own discretion and risk.
                  <br />
                  <br />
                  SecurXpert may modify, update or discontinue products and services
                  at any time without prior notice. Users are responsible for
                  evaluating product suitability before purchase or use.
                </>
              ),
            },
            {
              title: '11. Privacy Policy',
              content: (
                <>
                  By using our services, you also agree to our Privacy Policy, which
                  explains how we collect, store and protect your personal data. We
                  use secure encryption and industry-standard measures for data
                  protection.
                </>
              ),
            },
            {
              title: '12. Suspension or Termination',
              content: (
                <>
                  We may suspend or terminate access to our services if:
                  <br />
                  <br />
                  • You violate these terms
                  <br />
                  • Illegal or harmful activity is detected
                  <br />
                  • We deem it necessary for operational or legal reasons
                  <br />
                  <br />
                  Users may stop using our services at any time.
                </>
              ),
            },
            {
              title: '13. Changes to These Terms',
              content: (
                <>
                  SecurXpert may update or modify these terms from time to time.
                  Changes will be posted on the website or mobile app, and continued
                  use after modifications implies acceptance.
                </>
              ),
            },
            {
              title: '14. Governing Law &amp; Dispute Resolution',
              content: (
                <>
                  These terms are governed by the laws of India.
                  <br />
                  <br />
                  Any disputes will be settled through arbitration or legal
                  proceedings in Hyderabad, Telangana.
                </>
              ),
            },
            {
              title: '15. Contact Information',
              content: (
                <>
                  For questions or support, reach us at:
                  <br />
                  <br />
                  <strong>SecurXpert Technologies Pvt Ltd</strong>
                  <br />
                  5th Floor, Krishna Towers (Square Hub),
                  <br />
                  100 Feet Road, Madhapur,
                  <br />
                  Hyderabad, Telangana 500081
                  <br />
                  Phone: +91 7993256679
                  <br />
                  Email:{' '}
                  <a
                    href="mailto:info@SecurXperts.com"
                    className="text-cyan-600 hover:text-cyan-500"
                  >
                    info@SecurXperts.com
                  </a>
                  <br />
                  <br />
                  <strong>UK Office:</strong>
                  <br />
                  SecurXpert Technologies Pvt Ltd
                  <br />
                  194 Gulson Road,
                  <br />
                  Coventry, England, CV1 2JD
                  <br />
                  United Kingdom
                  <br />
                  <br />
                  By using our platform, you acknowledge that you have read and
                  understood these Terms and Conditions. Your continued use
                  constitutes acceptance of any updates made to these terms.
                </>
              ),
            },
          ].map((section, index) => (
            <div
              className="section bg-gray-100/80 p-4 sm:p-6 mb-8 sm:mb-10 border-b border-cyan-400/20 sm:border-0 hover:bg-gray-200/80 hover:scale-105 hover:shadow-xl transition-all duration-500 ease-in-out"
              key={index}
              data-index={index + 1}
              ref={(el) => (itemRefs.current[index + 1] = el)}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <h2
                className="text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-blue-900 no-underline mb-4 tracking-tight"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {section.title}
              </h2>
              <p
                className="text-sm xs:text-base sm:text-lg font-normal text-gray-800 no-underline leading-relaxed"
                data-aos="fade-up"
                data-aos-delay={(index + 1) * 100}
              >
                {section.content}
              </p>
            </div>
          ))}
        </div>
      </section>

      <Footer />

      {/* Back to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 bg-gradient-to-r from-blue-500 to-blue-700 p-3 sm:p-4 rounded-full shadow-xl hover:from-blue-600 hover:to-blue-800 transition"
        aria-label="Back to top"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 sm:w-6 h-5 sm:h-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>
    </div>
  );
};

export default TermsAndConditions;
